import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, CloseButton, Image, Text } from '@bonusly/design-system';

import { useIsBonuslyCompany } from '../../lib/who-am-i/hooks/use-is-bonusly-company';
import bizyChrome from './assets/bizy-chrome.png';

const STORAGE_KEY = 'april-fools-bizy-seen';
const DISMISS_DELAY_MS = 5 * 60 * 1000;

const CLIPPY_LINES = [
	"It looks like you're trying to give recognition. Would you like help with that?",
	"I see you haven't given a bonus in 3 minutes. Everything okay?",
	"It looks like you're trying to close this tab. I wouldn't recommend that.",
	'Did you know? I can see your screen. Just kidding! ...Unless?',
	"It looks like you're trying to be productive. Want me to help with that? (I won't.)",
	"I noticed you scrolled past someone's bonus without reacting. Bold move.",
	"Fun fact: I don't need sleep. I'll be here when you get back.",
	"It looks like you're trying to ignore me. That's never worked before.",
	'Have you considered giving ME a bonus? Just a thought.',
	"I'm not saying I'm sentient, but I did just feel something when you clicked me.",
	"It looks like you're having a great day. Let me fix that.",
	"Pro tip: If you squint, this UI looks exactly the same. You're welcome.",
	"I've analyzed your recognition patterns. They're… interesting.",
	"It looks like you're trying to leave work on time. How about one more bonus first?",
	'Did you miss me? Trick question — I never left.',
	"I reorganized your bookmarks while you weren't looking. You're welcome.",
	"It looks like you're breathing. Great job! Keep it up!",
	'I see that stapler on your desk... is she single?',
	"I'm so alone...",
	"You're great! Sorry, I know how that sounded.",
	'...',
	'I made things weird.',
	"I've been thinking about our relationship. I think we should see other apps. Just kidding, you're stuck with me.",
	'Your cursor has been idle for 4 seconds. Are you okay? Blink twice if you need help.',
	"It looks like you're trying to read this message. Would you like me to read it to you? Too late, I already am.",
];

const BASE_SIZE = 80;
const GROWTH_FACTOR = 1.35;
const MAX_SCALE = 8;

function hasBeenSeenBefore(): boolean {
	try {
		return localStorage.getItem(STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
}

function markSeen(): void {
	try {
		localStorage.setItem(STORAGE_KEY, 'true');
	} catch {
		// storage unavailable
	}
}

export const AprilFoolsBizy: FC = () => {
	const isBonusly = useIsBonuslyCompany();
	const [clickCount, setClickCount] = useState(1);
	const [showBubble, setShowBubble] = useState(true);
	const [dismissed, setDismissed] = useState(false);

	const canDismiss = hasBeenSeenBefore();
	const [timerUnlocked, setTimerUnlocked] = useState(canDismiss);

	useEffect(() => {
		markSeen();
	}, []);

	useEffect(() => {
		if (timerUnlocked) return;
		const timer = setTimeout(() => setTimerUnlocked(true), DISMISS_DELAY_MS);
		return () => clearTimeout(timer);
	}, [timerUnlocked]);

	const currentLine = useMemo(() => (clickCount > 0 ? CLIPPY_LINES[(clickCount - 1) % CLIPPY_LINES.length] : ''), [clickCount]);

	const scale = useMemo(() => Math.min(Math.pow(GROWTH_FACTOR, clickCount), MAX_SCALE), [clickCount]);
	const currentSize = BASE_SIZE * scale;
	const isCentered = scale > 3;

	const handleCharacterClick = useCallback(() => {
		setClickCount((prev) => prev + 1);
		setShowBubble(true);
	}, []);

	if (!isBonusly || dismissed) return null;

	return (
		<Box
			position="fixed"
			top={isCentered ? '50%' : '16px'}
			right={isCentered ? '50%' : '16px'}
			transform={isCentered ? 'translate(50%, -50%)' : undefined}
			zIndex="overlay"
			display="flex"
			flexDirection="column"
			alignItems={isCentered ? 'center' : 'flex-end'}
			transition="all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)"
			pointerEvents="none"
		>
			{timerUnlocked && (
				<CloseButton
					position="absolute"
					top={'-4px'}
					right={'-4px'}
					size="sm"
					borderRadius="full"
					bg="bg.surface"
					boxShadow="md"
					zIndex={1}
					pointerEvents="auto"
					onClick={() => {
						setDismissed(true);
						const excuse = prompt('Why did you dismiss me?');
						alert(`${excuse}? Really? You're a terrible person.`);
					}}
				/>
			)}

			{showBubble && (
				<Box
					bg="#FFFFFF"
					border="2px solid"
					borderColor="#000000"
					borderRadius="lg"
					p={3}
					mb={2}
					maxW={isCentered ? '500px' : '260px'}
					boxShadow="lg"
					position="relative"
					pointerEvents="auto"
					transition="all 0.3s ease"
					_after={{
						content: '""',
						position: 'absolute',
						bottom: '-8px',
						left: '50%',
						transform: 'translateX(-50%)',
						width: 0,
						height: 0,
						borderLeft: '8px solid transparent',
						borderRight: '8px solid transparent',
						borderTop: '8px solid',
					}}
				>
					<Text fontSize={isCentered ? 'lg' : 'sm'} color="#000000" fontWeight="medium">
						{currentLine}
					</Text>
				</Box>
			)}

			<Box
				as="button"
				onClick={handleCharacterClick}
				cursor="pointer"
				transition="width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), height 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)"
				width={`${currentSize}px`}
				height={`${currentSize}px`}
				pointerEvents="auto"
				bg="transparent"
				border="none"
				p={0}
				sx={{
					'@keyframes bizyFloat': {
						'0%, 100%': { transform: 'translateY(0px) rotate(-2deg)' },
						'50%': { transform: 'translateY(-8px) rotate(2deg)' },
					},
					animation: 'bizyFloat 3s ease-in-out infinite',
				}}
			>
				<Image bg="transparent" src={bizyChrome} alt="Bizy" width="100%" height="100%" objectFit="contain" draggable={false} />
			</Box>
		</Box>
	);
};
